import { Fragment } from "react/jsx-runtime";
import MultiStepForm from "../MultiStepForm";
import { forwardRef, useContext, useEffect, useRef, useState } from "react";
import CustomPopUp from "../CustomPopup";
import TestSubmissionPopup from "../../popups/testSubmissionPopup";
import OnlineOrderValuesModel from "../../../services/models/formModels/onlineOrderValues.model";
import { OrderStepsContext } from "../../../services/context/OrderStepsContext";
import useProfile from "../../../services/utils/custom hooks/useProfile";
import { OrderStepActions } from "../../../services/reducers/OrderStepsReducer";
import { useHistory, useLocation } from "react-router-dom";
import Button from "../../organism/Button";
import CustomText from "../../atomic/CustomText";
import OrderDiscardPopup from "../../popups/OrderDiscardPopup";
import DeleteOrderPopup from "../../popups/deleteOrderPopup";
import { useAdminContext } from "../../../services/context/TourPopupContext";
interface OnlineOrderFromProps {
  initialValues: OnlineOrderValuesModel | Object;
  isPending: boolean;
  mutate: Function;
  isUpdate: boolean;
  shouldValidate: boolean;
  preventDiscardPopup: boolean;
  setPreventDiscardPopup: any;
}

function OnlineOrderForm(
  {
    initialValues,
    isPending,
    mutate,
    isUpdate,
    shouldValidate,
    preventDiscardPopup,
    setPreventDiscardPopup,
  }: OnlineOrderFromProps,
  ref: any
): JSX.Element {
  const {
    state: { orderSteps },
    dispatch,
  } = useContext(OrderStepsContext);
  const profile = useProfile();
  const [submissionPopupVisible, setSubmissionPopupVisible] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState<boolean>(false);
  const [routeChange, setRouteChange] = useState(false);
  const location = useLocation();
  const history: any = useHistory();
  const nextLocationRef: any = useRef(null);
  const unblockRef: any = useRef(null);

  function customFormSubmission(values: OnlineOrderValuesModel) {
    /**
     * Role based form submission
     * Determine if dual role:
     * If yes, determine if the selected physician is himself
     * If yes, do not show the submission popup and directly submit the popup
     * If no, show the submission popup
     */

    const ssnMrnMissing =
      ((values as OnlineOrderValuesModel)?.testTypeId === 4 || (values as OnlineOrderValuesModel)?.testTypeId === 7) &&
      !(values as OnlineOrderValuesModel)?.patientInfo?.securityInfo?.ssn &&
      !(values as OnlineOrderValuesModel)?.patientInfo?.securityInfo?.mrn;

    const roles = profile?.data?.user?.roles || [];
    const isDualRolePhysician =
      roles.length === 2 && roles.includes("Physician");
    const sameAsSelectedPhysician =
      Number(values.physicianId) === profile.data?.user?.id;

    const directSubmission =
      (roles.includes("Physician") && roles.length === 1) ||
      (isDualRolePhysician && sameAsSelectedPhysician) || (roles?.includes('Collaborative Order Agreement'));

    if (directSubmission && !ssnMrnMissing) {
      setPreventDiscardPopup(true);
      // If the user has the Collaborative Order Agreement role, don't show the popup

      if (values.testTypeId === 8) {
        setSubmissionPopupVisible(true);
      } else {
        ref.current.handleSubmit();
      }
    } else {
      setSubmissionPopupVisible(true);
      setPreventDiscardPopup(true);
    }
  }


  const handlePopupClose = (confirmLeave: any) => {
    setRouteChange(false);
    if (confirmLeave && nextLocationRef.current) {
      if (unblockRef.current) {
        unblockRef.current(); // Correctly invoke unblock as a function
      }
      history.push(nextLocationRef.current.pathname); // Navigate to the stored location
    }
  };

  useEffect(() => {
    if (isUpdate || shouldValidate) {
      const hash = location.hash;
      const clinicians = (initialValues as OnlineOrderValuesModel)
        .additionalClinician.length;
      if (clinicians)
        dispatch({
          type: OrderStepActions.ADD_EXISTING_CLINICIAN,
          payload: clinicians,
        });
      if (hash) {
        const elementId = hash.substring(1);
        const element = document.getElementById(
          elementId.replace("#", "").replace("%20", " ")
        );
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth" });
          }, 1000);
        }
      }
    }
    if (
      !preventDiscardPopup &&
      !!history?.location?.state?.tourPopup === false
    ) {
      unblockRef.current = history.block((nextLocation: any) => {
        nextLocationRef.current = nextLocation; // Store the next location
        if (
          nextLocation?.pathname?.includes("online-order") ||
          nextLocation?.state?.skipDiscardPopup
        ) {
          setRouteChange(false);
        } else {
          setRouteChange(true); // Show the popup
          return false; // Prevent navigation
        }
      });
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current(); // Cleanup the block when the component unmounts
      }
    };
  }, [history, preventDiscardPopup]);

  return (
    <Fragment>
      <MultiStepForm
        ref={ref}
        initialValues={initialValues}
        initialTouchedValues={{
          patientInfo: {
            firstName: true,
            lastName: true,
            middleInitial: false,
            birthDate: true,
            email: false,
            address: false,
            gender: true,
            phone: false,
            city: false,
            state: false,
            securityInfo: {
              ssn: true,
              mrn: false,
            },
            zipcode: false,
          },
          guarantorName: true,
        }}
        submitting={isPending}
        onSubmit={(values: any): void => {
          setPreventDiscardPopup(true);
          mutate(values);
        }}
        isUpdate={isUpdate}
        shouldValidate={shouldValidate}
        customSubmissionFunction={customFormSubmission}
        handleDeleteOrder={() => {
          setPreventDiscardPopup(true);
          setDeletePopupVisible(true);
        }}
        showProgressBar
        formContainerClassName="grid grid-rows-twoRowLayout md:grid-rows-none md:grid-cols-twoColumnSettingsTabletPageLayout lg:grid-cols-twoColumnSettingsPageLayout overflow-hidden "
        sectionContainerClassName={`w-full ${history?.location?.state?.tourPopup
          ? "overflow-hidden"
          : "overflow-y-auto"
          }`}
        stepContainerClassName="bg-sideNavGray border-r py-4 px-2 pl-3 overflow-hidden flex overflow-x-auto md:block"
        fieldsContainerClass={`bg-white p-6 shadow-md rounded-md hover:border-primary border `}
        globalSectionClassName="px-3 md:px-6 lg:px-[109px] scroll-my-24 mb-6"
        steps={orderSteps}
        groupFields={[
          "billingInfo.phone",
          "billingInfo.policyNumber",
          "npi",
          "fax",
          "laboratoryInfo.dateOfCollection",
          "laboratoryInfo.specimenId",
          "laboratoryInfo.labortaryFax",
          "laboratoryInfo.labortaryPhone",
          "supportingDocs.pathologyReport",
          "supportingDocs.insuranceCard",
          "supportingDocs.demographicsSheet",
          "tubeBarcode",
          "historyPhysicalExam",
          "pathologySurgicalFindings",
          // "patientInfo.firstName",
          // "patientInfo.middleInitial",
          // "patientInfo.lastName",
          "patientInfo.birthDate",
          "patientInfo.gender",
          "patientInfo.state",
          "patientInfo.city",
          "patientInfo.zipcode",
          "patientInfo.securityInfo.ssn",
          "patientInfo.securityInfo.mrn",
        ]}
      />
      {routeChange &&
        !preventDiscardPopup &&
        !history?.location?.state?.tourPopup && (
          <CustomPopUp
            hidePopUp={() => handlePopupClose(false)}
            popUpTitle={"Online Order"}
            onlineOrder={false}
            hideClose={false}
          >
            <OrderDiscardPopup handlePopupClose={handlePopupClose} />
          </CustomPopUp>
        )}
      {submissionPopupVisible && (
        <CustomPopUp
          hidePopUp={() => setSubmissionPopupVisible(false)}
          popUpTitle={"Test Submission - Authorization Required"}
          onlineOrder={false}
          hideClose={false}
        >
          <TestSubmissionPopup
            initialValues={{ ...ref?.current?.values }}
            mutate={mutate}
            isPending={isPending}
          />
        </CustomPopUp>
      )}
      {deletePopupVisible && isUpdate && (initialValues as any)?.id && (
        <CustomPopUp
          hidePopUp={() => setDeletePopupVisible(false)}
          popUpTitle={"Online Order"}
          onlineOrder={false}
          hideClose={false}
        >
          <DeleteOrderPopup
            orderId={(initialValues as any).id}
            closePopup={() => setDeletePopupVisible(false)}
          />
        </CustomPopUp>
      )}
    </Fragment>
  );
}

export default forwardRef(OnlineOrderForm);
